import { useState, useEffect, useCallback } from "react";
import { X, Download } from "lucide-react";

const SESSION_KEY = "mmntm_session_count";
const DISMISS_KEY = "mmntm_install_dismissed_at";
const MIN_SESSIONS = 5;
const DISMISS_DAYS = 30;

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Increment session count
    const count = parseInt(localStorage.getItem(SESSION_KEY) || "0", 10) + 1;
    localStorage.setItem(SESSION_KEY, String(count));

    // Check dismiss timestamp
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_DAYS) return;
    }

    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (count >= MIN_SESSIONS) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") setShow(false);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[calc(100%-2rem)] animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt">
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl"
        style={{ background: "linear-gradient(135deg, #1a1a2e, #16213e)", border: "1px solid rgba(168,85,247,0.3)" }}>
        <Download className="h-5 w-5 shrink-0 text-purple-400" />
        <span className="text-sm text-white/90 flex-1">Install MMNTM for a faster experience</span>
        <button onClick={handleInstall}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-purple-500 text-white hover:bg-purple-400 transition-colors shrink-0"
          data-testid="install-prompt-install">
          Install
        </button>
        <button onClick={handleDismiss}
          className="p-1 rounded hover:bg-white/10 transition-colors shrink-0"
          data-testid="install-prompt-dismiss">
          <X className="h-4 w-4 text-white/40" />
        </button>
      </div>
    </div>
  );
}
